import type { DealStage } from '@elka/theme';
import type { Deal } from '@elka/types';

/** The subset of a deal's fields the stage-transition invariants read/write. */
export interface StageTransitionDeal {
  actualCloseDate?: string;
  lostReason?: string;
}

/**
 * Computes the actualCloseDate/lostReason patch for a stage transition. Single
 * source of truth for the invariants every stage-change surface (DealForm,
 * kanban drag) must maintain: stamp actualCloseDate on the first transition into
 * won/lost (if not already set), clear it on the transition back out to an open
 * stage, and drop a stale lostReason once a deal is won.
 */
export function stagePatch(
  prevStage: DealStage,
  nextStage: DealStage,
  deal: StageTransitionDeal
): { stage: DealStage; actualCloseDate?: string; lostReason?: string } {
  const wasClosed = prevStage === 'won' || prevStage === 'lost';
  const isNowClosed = nextStage === 'won' || nextStage === 'lost';

  if (!wasClosed && isNowClosed) {
    const todayStr = new Date().toISOString().split('T')[0];
    return {
      stage: nextStage,
      actualCloseDate: deal.actualCloseDate || todayStr,
      lostReason: nextStage === 'lost' ? deal.lostReason || '' : '',
    };
  }
  if (wasClosed && !isNowClosed) {
    return { stage: nextStage, actualCloseDate: '', lostReason: '' };
  }
  if (isNowClosed && nextStage === 'won') {
    // lost -> won: keep the close date, drop the stale lostReason.
    return { stage: nextStage, lostReason: '' };
  }
  return { stage: nextStage };
}

export const STAGE_PROBABILITY: Record<DealStage, number> = {
  lead: 10,
  qualified: 25,
  proposal: 50,
  negotiation: 75,
  won: 100,
  lost: 0,
};

/** Quick stage change (board drag, stage rail, quick menu): stagePatch + the stage's default probability. */
export function stageChangePatch(
  deal: Pick<Deal, 'stage' | 'actualCloseDate' | 'lostReason'>,
  next: DealStage
) {
  return { ...stagePatch(deal.stage, next, deal), probability: STAGE_PROBABILITY[next] };
}
